import { useState, useEffect } from 'react';
import axios from 'axios';
import { CHART_COLORS } from './homeConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_LABELS = {
  OPEN: 'Open', IN_PROCESS: 'In Process', DRAFT: 'Draft',
  PARTIALLY_DELIVERED: 'Part. Delivered', DELIVERED: 'Delivered',
  COMPLETED: 'Completed', CANCELLED: 'Cancelled',
};

const soValue = (so) => so.gross_value || so.net_value || 0;

/**
 * Loads and aggregates real home-dashboard data from sales orders, purchase
 * orders, AR aging and the recent-activities feed.
 */
export default function useHomeData() {
  const [state, setState] = useState({
    loading: true,
    kpis: { totalOrders: 0, revenue: 0, revenueTrend: null, pendingActions: 0, arOutstanding: 0 },
    revenueData: [],
    ordersByStatus: [],
    arAging: [],
    recentActivities: [],
  });

  useEffect(() => {
    let active = true;
    (async () => {
      const [soRes, poRes, arRes, actRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/otc/sales-orders`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/purchase-orders`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/ar/ar-aging`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/recent-activities?limit=8`).catch(() => ({ data: [] })),
      ]);
      if (!active) return;

      const sos = soRes.data || [];
      const pos = poRes.data || [];
      const ar = arRes.data || [];

      // KPIs
      const revenue = sos.reduce((sum, so) => sum + soValue(so), 0);
      const openSO = sos.filter((s) => ['OPEN', 'IN_PROCESS', 'DRAFT', 'PARTIALLY_DELIVERED'].includes(s.status)).length;
      const openPO = pos.filter((p) => p.status === 'OPEN').length;
      const arOutstanding = ar.reduce((sum, r) => sum + (r.total_open || 0), 0);

      // Revenue by month — last 6 months ending this month
      const now = new Date();
      const buckets = [];
      const keyToIdx = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        keyToIdx[key] = buckets.length;
        buckets.push({ label: MONTH_LABELS[d.getMonth()], key, revenue: 0, orders: 0 });
      }
      sos.forEach((so) => {
        const key = (so.document_date || '').slice(0, 7);
        const idx = keyToIdx[key];
        if (idx !== undefined) {
          buckets[idx].revenue += soValue(so);
          buckets[idx].orders += 1;
        }
      });
      const lastRev = buckets[buckets.length - 1].revenue;
      const prevRev = buckets[buckets.length - 2]?.revenue || 0;
      const revenueTrend = prevRev > 0 ? Math.round(((lastRev - prevRev) / prevRev) * 100) : null;

      // Orders by status (donut)
      const statusCount = {};
      sos.forEach((so) => { statusCount[so.status] = (statusCount[so.status] || 0) + 1; });
      const ordersByStatus = Object.entries(statusCount)
        .sort((a, b) => b[1] - a[1])
        .map(([status, value], i) => ({
          name: STATUS_LABELS[status] || status,
          value,
          color: CHART_COLORS.donut[i % CHART_COLORS.donut.length],
        }));

      // AR aging buckets (bar)
      const sumBucket = (field) => ar.reduce((s, r) => s + (r[field] || 0), 0);
      const arAging = [
        { bucket: 'Current', amount: sumBucket('current') },
        { bucket: '1-30', amount: sumBucket('days_1_30') },
        { bucket: '31-60', amount: sumBucket('days_31_60') },
        { bucket: '61-90', amount: sumBucket('days_61_90') },
        { bucket: '90+', amount: sumBucket('days_over_90') },
      ];

      setState({
        loading: false,
        kpis: { totalOrders: sos.length, revenue, revenueTrend, pendingActions: openSO + openPO, arOutstanding },
        revenueData: buckets,
        ordersByStatus,
        arAging,
        recentActivities: actRes.data || [],
      });
    })();
    return () => { active = false; };
  }, []);

  return state;
}
